import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { CircularProgress, Box } from '@mui/material';

import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';

import { getIssues } from '../redux/IssueCRUD';
import { InventoryIssueRow } from '../types';
import IssueList from './IssueList';

export type IssueOverviewListHandle = {
  requestRefresh: (options?: { showSuccessMessage?: boolean }) => void;
};

interface IssueOverviewListProps {
  companyId?: string;
  searchQuery?: string;
  status?: string;
  onSelectRow: (row: InventoryIssueRow) => void;
  onFetchError?: (message: string) => void;
  onRefreshSuccess?: () => void;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveListCompanyId = (companyId?: string) => {
  const resolved = (companyId || getCurrentCompanyId() || '').trim();
  if (!resolved || resolved === 'all' || resolved === 'global' || !UUID_PATTERN.test(resolved)) {
    return undefined;
  }
  return resolved;
};

const IssueOverviewList = forwardRef<IssueOverviewListHandle, IssueOverviewListProps>(function IssueOverviewList(
  { companyId, searchQuery = '', status = 'all', onSelectRow, onFetchError, onRefreshSuccess },
  ref
) {
  const [rows, setRows] = useState<InventoryIssueRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [showSuccessOnLoad, setShowSuccessOnLoad] = useState(false);
  const onFetchErrorRef = useRef(onFetchError);
  const onRefreshSuccessRef = useRef(onRefreshSuccess);

  onFetchErrorRef.current = onFetchError;
  onRefreshSuccessRef.current = onRefreshSuccess;

  const resolvedCompanyId = useMemo(() => resolveListCompanyId(companyId), [companyId]);

  useEffect(() => {
    let active = true;

    const fetchRows = async () => {
      setLoading(true);
      try {
        const response = await getIssues({
          companyId: resolvedCompanyId,
          search: searchQuery || undefined,
          status: status !== 'all' ? status : undefined,
        });

        if (!active) {
          return;
        }

        setRows(response.results || []);

        if (showSuccessOnLoad) {
          onRefreshSuccessRef.current?.();
          setShowSuccessOnLoad(false);
        }
      } catch (error: unknown) {
        if (!active) {
          return;
        }

        setRows([]);
        setShowSuccessOnLoad(false);
        const message =
          error && typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string'
            ? (error as { message: string }).message
            : 'Khong the tai danh sach phieu xuat kho.';
        onFetchErrorRef.current?.(message);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    const timeout = window.setTimeout(() => {
      void fetchRows();
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [resolvedCompanyId, searchQuery, status, reloadTick, showSuccessOnLoad]);

  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: (options) => {
        if (options?.showSuccessMessage) {
          setShowSuccessOnLoad(true);
        }
        setReloadTick((current) => current + 1);
      },
    }),
    []
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return <IssueList rows={rows} onSelectRow={onSelectRow} />;
});

export default IssueOverviewList;
